// Post-login return target.
//
// When an unauthenticated user lands on a deep link (e.g. scanning a QR label
// → `/lva/<code>?c=<companyId>`), RequireAuth bounces them to `/login` and the
// MSAL `loginRedirect` does a full page reload to Entra and back. In-memory
// React state cannot survive that round-trip, so we stash the intended path in
// `sessionStorage` (which MSAL itself uses here, so it persists for the tab
// across the Entra bounce). After auth resolves, `/auth/callback` restores it.
//
// Only same-origin relative paths are accepted (must start with a single `/`)
// and the auth pages themselves are never stored — this avoids both
// open-redirect to an external origin and a redirect loop back into /login.

const RETURN_PATH_STORAGE_KEY = 'dcc-erp/post-login-return';

const BLOCKED_PREFIXES = ['/login', '/auth/'];

/** A path is safe to return to only if it is a same-origin absolute path. */
export function isSafeReturnPath(path: string | null | undefined): path is string {
  if (!path) return false;
  // Reject protocol-relative (`//host`) and absolute URLs — only `/path…`.
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  if (path.startsWith('/\\')) return false; // `/\evil.com` normalises to a host in some browsers
  return !BLOCKED_PREFIXES.some((p) => path === p || path.startsWith(p));
}

/** Stash the intended path (pathname + search) for restoration after login. */
export function saveReturnPath(path: string): void {
  if (typeof window === 'undefined') return;
  if (!isSafeReturnPath(path)) return;
  try {
    window.sessionStorage.setItem(RETURN_PATH_STORAGE_KEY, path);
  } catch {
    // sessionStorage may be unavailable (private mode / blocked) — degrade to
    // the default post-login destination rather than throwing.
  }
}

/**
 * Convenience wrapper: stash the CURRENT location (pathname + search) as the
 * post-login return target. Call this right before an interactive
 * `acquireTokenRedirect` — once auth resolves at `/auth/callback`, the user is
 * bounced back to the page they were on instead of the default `/dashboard`.
 *
 * Safe to call unconditionally from anywhere: `saveReturnPath` ignores unsafe
 * and auth-page paths (so a redirect fired while already on `/login` won't
 * clobber an earlier, more specific target).
 */
export function saveCurrentPathForReturn(): void {
  if (typeof window === 'undefined') return;
  saveReturnPath(window.location.pathname + window.location.search);
}

/**
 * Read and clear the stashed return path. Returns null when nothing safe was
 * stored. Single-use so a stale target can't hijack a later, unrelated login.
 */
export function takeReturnPath(): string | null {
  if (typeof window === 'undefined') return null;
  let stored: string | null;
  try {
    stored = window.sessionStorage.getItem(RETURN_PATH_STORAGE_KEY);
    window.sessionStorage.removeItem(RETURN_PATH_STORAGE_KEY);
  } catch {
    return null;
  }
  return isSafeReturnPath(stored) ? stored : null;
}
